import { rememberDeviceSignIn } from '../../auth/deviceSignIn';
import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { configureLegacyApiBaseUrl } from '../../config/legacyApi';

configureLegacyApiBaseUrl();

// Utility to add JWT
const setAuthHeader = token => {
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
};

// Utility to remove JWT
const clearAuthHeader = () => {
  axios.defaults.headers.common.Authorization = '';
};

/*
 * POST @ /users/register
 * body: { name, email, password }
 */
export const register = createAsyncThunk(
  'auth/register',
  async (credentials, thunkAPI) => {
    try {
      const res = await axios.post('api/users/register', credentials);
      // After successful registration, add the token to the HTTP header
      setAuthHeader(res.data.token);
      rememberDeviceSignIn();
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

/*
	Google Cloud Console mykhailo.stryzhka@anirakids.com
 * POST @ /users/authByGoogle
 * body: { credential }
 */
export const authByGoogle = createAsyncThunk(
  'auth/authByGoogle',
  async ({ credential }, thunkAPI) => {
    try {
      const res = await axios.post('api/users/authByGoogle', {
        credential,
      });
      // After successful registration, add the token to the HTTP header
      setAuthHeader(res.data.token);
      rememberDeviceSignIn();
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

/*
 * POST https://login.szn.cz/api/v1/oauth/token
 * body: { code, redirect_uri }
 */
export const authBySeznam = createAsyncThunk(
  'auth/authBySeznam',
  async (credentials, thunkAPI) => {
    try {
      const res = await axios.post('api/users/authBySeznam', credentials);

      // After successful registration, add the token to the HTTP header
      setAuthHeader(res.data.token);
      rememberDeviceSignIn();

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

/*
 * POST @ /users/login
 * body: { email, password }
 */
export const logIn = createAsyncThunk(
  'auth/login',
  async (credentials, thunkAPI) => {
    try {
      const res = await axios.post('/api/users/login', credentials);
      // After successful login, add the token to the HTTP header
      setAuthHeader(res.data.token);
      rememberDeviceSignIn();

      return res.data;
    } catch (error) {

      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

/*
 * POST @ /users/logout
 * headers: Authorization: Bearer token
 */
export const logOut = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  try {
    await axios.post('/api/users/logout');
    // After a successful logout, remove the token from the HTTP header
    clearAuthHeader();
  } catch (error) {
    if (error.response?.status === 401) { clearAuthHeader(); return; }
    return thunkAPI.rejectWithValue(error.message);
  }
});

/*
 * GET @ /users/current
 * headers: Authorization: Bearer token
 */
export const refreshUser = createAsyncThunk(
  'auth/refresh',
  async (_, thunkAPI) => {
    // Reading the token from the state via getState()
    const state = thunkAPI.getState();
    const persistedToken = state.auth.token;

    if (persistedToken === null) {
      // If there is no token, exit without performing any request
      return thunkAPI.rejectWithValue('Unable to fetch user');
    }

    try {
      // If there is a token, add it to the HTTP header and perform the request
      setAuthHeader(persistedToken);
      const res = await axios.get('/api/users/current', { headers: { Authorization: `Bearer ${persistedToken}` } });
      rememberDeviceSignIn();
      return res.data;
    } catch (error) {
      if (error.response?.status === 401 && thunkAPI.getState().auth.token === persistedToken) clearAuthHeader();
      return thunkAPI.rejectWithValue({ status: error.response?.status || 0, message: 'Přihlášení se nepodařilo ověřit.' });
    }
  },
  { condition: (_, { getState }) => Boolean(getState().auth.token) && !getState().auth.isRefreshing }
);

/*
 * PATCH @ /api/users/current/update
 * headers: { 'content-type': 'multipart/form-data' }
 * body: {
      avatarUrl,
      firstName,
      lastName,
      companyName,
      nickname,
      primaryPhoneNumber,
      email,
      newPassword,
      confirmNewPassword,
      ico,
    }
 */
export const updateUserInfo = createAsyncThunk(
  'auth/update',
  async (
    {
      avatarUrl: avatar,
      firstName,
      lastName,
      companyName,
      nickname,
      primaryPhoneNumber,
      email,
      newPassword,
      confirmNewPassword,
      ico,
    },
    thunkAPI
  ) => {
    try {
      const formData = new FormData();
      avatar instanceof Blob && formData.append('avatar', avatar);
      firstName && formData.append('firstName', firstName);
      lastName && formData.append('lastName', lastName);
      companyName && formData.append('companyName', companyName);
      ico && formData.append('ico', ico);
      nickname && formData.append('nickname', nickname);
      primaryPhoneNumber &&
        formData.append('primaryPhoneNumber', primaryPhoneNumber);
      email && formData.append('email', email);
      newPassword && formData.append('newPassword', newPassword);
      confirmNewPassword &&
        formData.append('confirmNewPassword', confirmNewPassword);

      const response = await axios.patch(
        `/api/users/current/update`,
        formData,
        { headers: { 'content-type': 'multipart/form-data' } }
      );

      return response.data;
    } catch (error) {

      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

export const updateUserEmail = createAsyncThunk(
  'auth/updateEmail',
  async ({ email }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `/api/users/current/refreshEmail?email=${encodeURIComponent(email)}`
      );


      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

export const verifiedEmail = createAsyncThunk(
  'auth/verifiedEmail',
  async (_, thunkAPI) => {
    try {
      const response = await axios.patch(`/api/users/current/verifiedEmail`);


      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status,
        message: error.response?.data?.message || 'Připojení se nezdařilo.',
      });
    }
  }
);

export const confirmUserEmail = createAsyncThunk(
  'auth/confirmEmail',
  async ({ token, changeEmail = false }, thunkAPI) => {
    if (!token) return thunkAPI.rejectWithValue({ status: 400, message: 'Missing confirmation token' });
    try {
      // A link must not overwrite the Authorization header of another signed-in account.
      const response = changeEmail
        ? await axios.post('/api/users/current/confirmEmailChange', { token })
        : await axios.post('/api/users/current/confirmEmail', { token });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        status: error.response?.status || 0,
        message: 'Email confirmation failed',
      });
    }
  }
);
